import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Wallet, RefreshCw, Scissors, TrendingUp, TrendingDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ withCredentials: true });

const DAY_LABELS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

const fmtDay = (iso, i) => {
  const [y, m, d] = iso.split("-").map(Number);
  return `${DAY_LABELS[i]} ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}`;
};

export default function WeekReport() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      http.get(`${API}/admin/report/week`),
      http.get(`${API}/admin/report/month`),
    ])
      .then(([w, m]) => {
        setData(w.data);
        setMonth(m.data);
      })
      .catch(() => toast.error("Erro ao carregar relatório."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <p className="py-16 text-center text-sm text-smoke" data-testid="report-loading">Carregando...</p>;
  }
  if (!data) return null;

  const maxRevenue = Math.max(...data.days.map((d) => d.revenue), 1);
  const ticket = data.total_count ? Math.round(data.total_revenue / data.total_count) : 0;

  return (
    <main className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="week-report">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-bebas text-4xl tracking-wide text-cream">Relatório da semana</h2>
          <p className="text-sm text-smoke mt-1">Agendamentos ativos (confirmados + pendentes), sem contar cancelados.</p>
        </div>
        <button
          onClick={load}
          data-testid="report-refresh-button"
          className="focus-gold inline-flex items-center gap-2 border border-white/15 text-smoke font-bebas text-lg tracking-widest px-5 py-2.5 hover:border-gold hover:text-gold transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-8" data-testid="report-stats">
        <div className="border border-white/10 bg-surface p-4 md:p-6">
          <p className="font-bebas text-3xl md:text-5xl text-gold flex items-center gap-3">
            <CalendarDays className="w-6 h-6 md:w-8 md:h-8" /> {data.total_count}
          </p>
          <p className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-smoke mt-2">Cortes na semana</p>
        </div>
        <div className="border border-white/10 bg-surface p-4 md:p-6">
          <p className="font-bebas text-3xl md:text-5xl text-green-400 flex items-center gap-3">
            <Wallet className="w-6 h-6 md:w-8 md:h-8" /> R$ {data.total_revenue}
          </p>
          <p className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-smoke mt-2">Faturamento previsto</p>
        </div>
        <div className="border border-white/10 bg-surface p-4 md:p-6">
          <p className="font-bebas text-3xl md:text-5xl text-cream flex items-center gap-3">
            <Scissors className="w-6 h-6 md:w-8 md:h-8" /> R$ {ticket}
          </p>
          <p className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-smoke mt-2">Ticket médio</p>
        </div>
      </div>

      {month && (
        <div className="mt-6 border border-white/10 bg-surface p-5 md:p-8" data-testid="month-report">
          <p className="text-xs uppercase tracking-[0.25em] text-smoke mb-6 capitalize">
            Mês a mês · {month.month_label} (agendado) vs {month.prev_month_label} (completo)
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              ["Cortes", month.current.count, month.previous.count, ""],
              ["Faturamento", month.current.revenue, month.previous.revenue, "R$ "],
            ].map(([label, cur, prev, prefix]) => {
              const delta = prev > 0 ? Math.round(((cur - prev) / prev) * 100) : cur > 0 ? 100 : 0;
              const up = delta >= 0;
              return (
                <div key={label} className="border border-white/10 bg-ink p-5" data-testid={`month-${label.toLowerCase()}`}>
                  <p className="text-xs uppercase tracking-[0.25em] text-smoke">{label}</p>
                  <div className="flex items-end gap-3 mt-3 flex-wrap">
                    <span className="font-bebas text-4xl text-cream">{prefix}{cur}</span>
                    <span className={`flex items-center gap-1 text-sm font-semibold ${up ? "text-green-400" : "text-red-400"}`}>
                      {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {Math.abs(delta)}%
                    </span>
                  </div>
                  <p className="text-xs text-smoke mt-2">{month.prev_month_label} completo: {prefix}{prev}</p>
                </div>
              );
            })}
          </div>
          {month.barbers.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-3" data-testid="month-barbers">
              {month.barbers.map((b) => (
                <span key={b.name} className="text-xs uppercase tracking-[0.2em] border border-white/10 px-4 py-2 text-smoke" data-testid={`month-barber-${b.name.toLowerCase()}`}>
                  {b.name}: <span className="text-gold">{b.count} cortes · R$ {b.revenue}</span> no mês
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-10 border border-white/10 bg-surface p-5 md:p-8" data-testid="report-days">
        <p className="text-xs uppercase tracking-[0.25em] text-smoke mb-6">Dia a dia</p>
        <div className="space-y-4">
          {data.days.map((d, i) => (
            <div key={d.date} className="flex items-center gap-4" data-testid={`report-day-${i}`}>
              <span className="w-28 shrink-0 text-sm text-smoke capitalize">{fmtDay(d.date, i)}</span>
              <div className="flex-1 h-7 bg-ink border border-white/10 relative overflow-hidden">
                <div
                  className="h-full bg-gold/80 transition-[width] duration-700"
                  style={{ width: `${Math.max((d.revenue / maxRevenue) * 100, d.count ? 4 : 0)}%` }}
                />
              </div>
              <span className="w-32 shrink-0 text-right font-bebas text-lg tracking-wide text-cream">
                {d.count} <span className="text-smoke text-sm">·</span> <span className="text-gold">R$ {d.revenue}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 border border-white/10 bg-surface p-5 md:p-8" data-testid="report-barbers">
        <p className="text-xs uppercase tracking-[0.25em] text-smoke mb-6">Por barbeiro</p>
        {data.barbers.length === 0 ? (
          <p className="text-sm text-smoke" data-testid="report-barbers-empty">Nenhum agendamento nesta semana ainda.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.barbers.map((b) => (
              <div key={b.name} className="flex items-center justify-between border border-white/10 bg-ink px-5 py-4" data-testid={`report-barber-${b.name.toLowerCase()}`}>
                <span className="font-bebas text-2xl tracking-wide text-cream">{b.name}</span>
                <span className="font-bebas text-xl text-gold">{b.count} cortes · R$ {b.revenue}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
